import { get, set } from 'lodash';
import uuid from 'uuid/v4';
import { CHANGE_PROCEDURE } from '@zocdoc-frontend-common/components-proceduredropdown';
import { WellGuideActions } from '../../Booking/actions/WellGuideActions';
import {
    AnalyticsTypes,
    DimensionKeys,
    SettingType,
    ContentGroupKeys,
} from '../../middleware/google-analytics';
import {
    GA_SEND_PAGE_VIEW,
    setLinkedDomain,
    setCustomDimension,
    setContentGroup,
    sendPageView,
} from '../../middleware/pageView/actions';
import { INITIAL_BOOKING_ROOT_KEY } from '../constants';
import {
    GaActionText,
    APPOINTMENT_INFO_GA_CATEGORY,
} from '../pages/AppointmentInfo/constants';
import { BookingCompletePageActions } from '../pages/BookingComplete/BookingCompletePageActions';
import { paths, basePath, BookingPathKeys } from '../paths';
import { AbGoals, OptimizelyAbMetrics } from '../utils';
import { LEGACY_LOCATION_CHANGE } from '../utils/logEvent';

const LOCATION_CHANGE = '@@router/LOCATION_CHANGE';
export const SIGN_IN_FORM_CATEGORY = 'Sign In';
export const CREATE_ACCOUNT_FORM_CATEGORY = 'Create Account';
export const BOOKING_PREFIX_CATEGORY = 'Booking - ';

export const GA_PAGES = {
    APPOINTMENT_DETAILS: `${basePath}/review`,
    APPOINTMENT_INFO: `${basePath}/start?`,
    BOOKING_COMPLETE: `${basePath}/complete`,
    CREATE_USERNAME: `${basePath}/createusername`,
    CREATE_PASSWORD: `${basePath}/createpassword`,
    PATIENT_INFO: `${basePath}/patientinfo`,
    PHONE_VERIFICATION: `${basePath}/verify`,
    REVIEW_AND_BOOK_PAGE: `${basePath}/reviewandbook`,
    SELECT_PATIENT: `${basePath}/patient`,
    SIGN_IN: `${basePath}/startsignin`,
    SIGN_UP: `${basePath}/signup`,
};

const attachAbGoalToChangeLocation = (action, pathname) => {
    if (pathname.includes(`${paths[BookingPathKeys.startSignInPath]}/`)) {
        action.meta = {
            analytics: {
                type: AnalyticsTypes.ChangePageView,
                page: GA_PAGES.SIGN_IN,
            },
        };
    } else if (
        pathname.includes(`${paths[BookingPathKeys.appointmentInfoPath]}/`)
    ) {
        action._abGoal = AbGoals.MadeItToStart;
        action.meta = {
            analytics: {
                type: AnalyticsTypes.ChangePageView,
                page: GA_PAGES.APPOINTMENT_INFO,
            },
        };
    } else if (
        pathname.includes(`${paths[BookingPathKeys.appointmentDetailsPath]}/`)
    ) {
        action._abGoal = AbGoals.MadeItToReview;
        action.meta = {
            analytics: {
                type: AnalyticsTypes.ChangePageView,
                page: GA_PAGES.APPOINTMENT_DETAILS,
            },
        };
    } else if (
        pathname.includes(`${paths[BookingPathKeys.selectPatientPath]}/`)
    ) {
        action._abGoal = AbGoals.MadeItToPatientSelect;
        action.meta = {
            analytics: {
                type: AnalyticsTypes.ChangePageView,
                page: GA_PAGES.SELECT_PATIENT,
            },
        };
    } else if (
        pathname.includes(`${paths[BookingPathKeys.phoneVerificationPath]}/`)
    ) {
        action._abGoal = AbGoals.MadeItToPhone;
        action.meta = {
            analytics: {
                type: AnalyticsTypes.ChangePageView,
                page: GA_PAGES.PHONE_VERIFICATION,
            },
        };
    } else if (
        pathname.includes(`${paths[BookingPathKeys.bookingCompletePath]}/`)
    ) {
        action._abGoal = AbGoals.MadeItToComplete;
        action.abMetric = OptimizelyAbMetrics.MadeItToBookingComplete;
        action.meta = {
            analytics: {
                type: AnalyticsTypes.ChangePageView,
                page: GA_PAGES.BOOKING_COMPLETE,
            },
        };
    } else if (
        pathname.includes(`${paths[BookingPathKeys.reviewAndBookPath]}/`)
    ) {
        action.abMetric = OptimizelyAbMetrics.MadeItToReviewAndBook;
        action.meta = {
            analytics: {
                type: AnalyticsTypes.ChangePageView,
                page: GA_PAGES.REVIEW_AND_BOOK_PAGE,
            },
        };
    } else if (
        pathname.includes(`${paths[BookingPathKeys.patientInfoPath]}/`)
    ) {
        action.meta = {
            analytics: {
                type: AnalyticsTypes.ChangePageView,
                page: GA_PAGES.PATIENT_INFO,
            },
        };
    } else if (pathname.includes(`${paths[BookingPathKeys.signUpPath]}/`)) {
        action.meta = {
            analytics: {
                type: AnalyticsTypes.ChangePageView,
                page: GA_PAGES.SIGN_UP,
            },
        };
    } else if (
        pathname.includes(`${paths[BookingPathKeys.createUsernamePath]}/`)
    ) {
        action.meta = {
            analytics: {
                type: AnalyticsTypes.ChangePageView,
                page: GA_PAGES.CREATE_USERNAME,
            },
        };
    } else if (
        pathname.includes(`${paths[BookingPathKeys.createPasswordPath]}/`)
    ) {
        action.meta = {
            analytics: {
                type: AnalyticsTypes.ChangePageView,
                page: GA_PAGES.CREATE_PASSWORD,
            },
        };
    } else if (pathname.includes(paths[BookingPathKeys.legacySyncPath])) {
        action._abGoal = AbGoals.MadeItToSync;
    }

    return action;
};

const setPageId = () => {
    set(window, 'ZD.configs.PAGE_ID', uuid());
};

export const bookingActionsAdapter = (store) => (next) => (action) => {
    if (
        action.meta &&
        action.meta.analytics &&
        (action.meta.analytics.category === SIGN_IN_FORM_CATEGORY ||
            action.meta.analytics.category === CREATE_ACCOUNT_FORM_CATEGORY)
    ) {
        action.meta.analytics.category = `${BOOKING_PREFIX_CATEGORY}${action.meta.analytics.category}`;
    }

    switch (action.type) {
        case CHANGE_PROCEDURE: {
            action.meta = {
                analytics: {
                    type: AnalyticsTypes.TrackEvent,
                    category: APPOINTMENT_INFO_GA_CATEGORY,
                    action: GaActionText.trackProcedurePickerSelection,
                },
            };
            break;
        }

        case LOCATION_CHANGE: {
            setPageId();
            action = attachAbGoalToChangeLocation(
                action,
                action.payload.pathname
            );
            if (action?.meta?.analytics) {
                const initialState = store.getState()[INITIAL_BOOKING_ROOT_KEY];
                action.meta.analytics.practiceId = initialState.practiceId;
            }
            break;
        }

        case LEGACY_LOCATION_CHANGE: {
            action = attachAbGoalToChangeLocation(
                action,
                action.payload.pathname
            );
            break;
        }

        case GA_SEND_PAGE_VIEW: {
            const initialState = store.getState()[INITIAL_BOOKING_ROOT_KEY];
            const whiteLabelUrl = get(initialState, 'whiteLabelUrl', '');

            if (initialState.logGaPageview) {
                store.dispatch(
                    setLinkedDomain({ domains: initialState.secondaryDomains })
                );

                store.dispatch(
                    setCustomDimension({
                        dimension: DimensionKeys.ZlogPageType,
                        value: 'Booking',
                    })
                );

                store.dispatch(
                    setCustomDimension({
                        dimension: DimensionKeys.UserExperienceTest,
                        value: initialState.isProductionTest,
                    })
                );

                store.dispatch(
                    setContentGroup({
                        group: ContentGroupKeys.ZlogPageType,
                        value: 'Booking',
                    })
                );

                store.dispatch(
                    setContentGroup({
                        group: ContentGroupKeys.ZlogRollupBucket,
                        value: 'Booking',
                    })
                );

                store.dispatch(
                    setCustomDimension({
                        dimension: DimensionKeys.BookingId,
                        value: initialState.bookingId,
                    })
                );

                if (whiteLabelUrl.split('/').length > 2) {
                    store.dispatch(
                        setCustomDimension({
                            dimension: DimensionKeys.WhiteLabel,
                            value: whiteLabelUrl.split('/')[2],
                        })
                    );
                }

                const pageViewEvent = {
                    page: 'Booking',
                    category: 'Booking',
                    isBot: initialState.isRobot,
                    isProductionTest: initialState.isProductionTest,
                    practiceId: initialState.practiceId,
                };
                store.dispatch(sendPageView(pageViewEvent));
            } else if (whiteLabelUrl.split('/').length > 2) {
                action.meta = {
                    analytics: {
                        type: AnalyticsTypes.SetDimension,
                        value: whiteLabelUrl.split('/')[2],
                        index: DimensionKeys.WhiteLabel,
                        settingType: SettingType.CustomDimension,
                    },
                };
            }
            break;
        }

        case WellGuideActions.TRACK_CLICK_NEW_BOOKING: {
            action.abMetric =
                OptimizelyAbMetrics.WellnessGuideBookingButtonClicked;
            action.meta = {
                analytics: {
                    type: AnalyticsTypes.TrackEvent,
                    action: GaActionText.trackClickNewBooking,
                },
            };
            break;
        }

        case BookingCompletePageActions.TRACK_CLICK_NEW_BOOKING: {
            action.abMetric =
                OptimizelyAbMetrics.BookingCompleteWellnessBookingButtonClicked;
            action.meta = {
                analytics: {
                    type: AnalyticsTypes.TrackEvent,
                    action: GaActionText.trackClickNewBooking,
                },
            };
            break;
        }

        case BookingCompletePageActions.TRACK_CLICK_CHECK_IN: {
            action.abMetric = OptimizelyAbMetrics.BookingCompleteCheckInClicked;
            break;
        }

        case BookingCompletePageActions.TRACK_CLICK_DOWNLOAD_APP: {
            action.abMetric = OptimizelyAbMetrics.ClickedDownloadTheApp;
            action.meta = {
                analytics: {
                    type: AnalyticsTypes.TrackEvent,
                    action: GaActionText.trackClickDownloadApp,
                },
            };
            break;
        }

        // no default
    }

    return next(action);
};
